import { useTranslation } from "react-i18next";

import { Text } from "@/components/ui/Text";
import { formatCutGroupValue } from "@/features/studies/report/storyline";

import type { CutResult } from "@/features/studies/report/storyline";

interface StorylineCutRowsProps {
  cut: CutResult;
}

type CutOutcome = NonNullable<CutResult["outcome"]>;

type AppTranslate = ReturnType<typeof useTranslation<"app">>["t"];

/**
 * What every group's value counts, from the cut's typed outcome: the same
 * composition as the deck's split charts (`cut_labels.value_label`). Null
 * when the outcome is absent (results stored before it) or names no option
 * for an option share.
 */
export function cutValueLine(outcome: CutOutcome, t: AppTranslate): string | null {
  const quoted = (text: string) => t("studyReport.storyline.cutValue.quoted", { text });
  let text: string;
  switch (outcome.reading) {
    case "option_share":
    case "picked_share": {
      const picked = outcome.reading === "picked_share";
      if (outcome.optionLabel != null) {
        text = picked
          ? t("studyReport.storyline.cutValue.sharePicked", { option: quoted(outcome.optionLabel) })
          : t("studyReport.storyline.cutValue.shareOption", { option: quoted(outcome.optionLabel) });
      } else if (outcome.itemLabels && outcome.itemLabels.length > 0) {
        text = picked
          ? t("studyReport.storyline.cutValue.shareEachPicked")
          : t("studyReport.storyline.cutValue.shareEachOption");
      } else {
        return null;
      }
      break;
    }
    case "ranking_first_share":
    case "ranking_last_share": {
      if (outcome.optionLabel == null) {
        return null;
      }
      text =
        outcome.reading === "ranking_first_share"
          ? t("studyReport.storyline.cutValue.rankedFirst", { option: quoted(outcome.optionLabel) })
          : t("studyReport.storyline.cutValue.rankedLast", { option: quoted(outcome.optionLabel) });
      break;
    }
    case "top_box_share":
      text = t("studyReport.storyline.cutValue.topBox");
      break;
    case "favorable_share":
      text = t("studyReport.storyline.cutValue.favorable");
      break;
    case "nps":
      text = t("studyReport.storyline.cutValue.nps");
      break;
    case "mean":
      text =
        outcome.scaleMin != null && outcome.scaleMax != null
          ? t("studyReport.storyline.cutValue.meanScale", {
              min: String(outcome.scaleMin),
              max: String(outcome.scaleMax),
            })
          : t("studyReport.storyline.cutValue.mean");
      break;
    default: {
      const unhandled: never = outcome.reading;
      return unhandled;
    }
  }
  return outcome.rowLabel
    ? t("studyReport.storyline.cutValue.forRow", { value: text, row: quoted(outcome.rowLabel) })
    : text;
}

/**
 * One computed cut, inline: a row per group with its label, base and value
 * at the cut's own decimals. A group under the directional floor shows its
 * count ("2 of 9"), never a percent. A paired cut adds how many moved up,
 * stayed or moved down; overlapping groups add a note.
 */
export function StorylineCutRows(props: StorylineCutRowsProps) {
  const { cut } = props;

  const { t } = useTranslation("app");

  const groups = cut.groups ?? [];
  const testId = `storyline-cut-${cut.requestId}`;
  const valueLine = cut.outcome ? cutValueLine(cut.outcome, t) : null;

  return (
    <div
      className="flex flex-col gap-1.5 rounded-lg border border-border px-3 py-2"
      data-testid={testId}
    >
      {valueLine ? (
        <Text
          size="xs"
          color="muted"
          className="leading-snug"
          data-testid={`${testId}-value-line`}
        >
          {valueLine}
        </Text>
      ) : null}
      {groups.map((group, index) => {
        const value = formatCutGroupValue(cut, group);
        const countOnly =
          value === null && typeof group.count === "number"
            ? t("studyReport.storyline.cutCountOnly", {
                count: group.count,
                base: group.base,
              })
            : null;
        return (
          <div
            key={group.groupId}
            className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-x-3 text-sm"
            data-testid={`${testId}-group-${index}`}
            data-counts-only={value === null ? "true" : undefined}
          >
            <span className="min-w-0 text-foreground">{group.label}</span>
            {/* A small group states its base inside its flag. */}
            <span className="text-xs tabular-nums text-muted-foreground">
              {group.thin
                ? t("studyReport.storyline.cutSmallGroup", { count: group.base })
                : t("studyReport.storyline.base", { count: group.base })}
            </span>
            <span
              className="min-w-12 text-right font-medium tabular-nums text-foreground"
              data-testid={`${testId}-group-${index}-value`}
            >
              {value ?? countOnly}
            </span>
          </div>
        );
      })}
      {cut.pairs ? (
        <Text
          size="xs"
          color="muted"
          className="leading-snug"
          data-testid={`${testId}-pairs`}
        >
          {t("studyReport.storyline.cutPaired", {
            up: cut.pairs.up,
            same: cut.pairs.same,
            down: cut.pairs.down,
          })}
        </Text>
      ) : null}
      {cut.overlap?.groupsOverlap ? (
        <Text
          size="xs"
          color="muted"
          className="leading-snug"
          data-testid={`${testId}-overlap`}
        >
          {t("studyReport.storyline.cutOverlap")}
        </Text>
      ) : null}
    </div>
  );
}
